// Pure, framework-free logic for the S3 BOM editor (Module 01 §4 S3, §2.6–2.9,
// §3.3). Everything here is decoupled from React and the network so the editor's
// load-bearing decisions — apportionment remainder math, the supersede preview,
// phantom-inlined tree reconstruction, and the version diff adaptation — are
// unit-tested without a DOM or the api-client. The screen components consume
// these; the tests exercise them directly.

import type { components } from "@assessly/api-client";
import type { DiffRow } from "../../components/version-switcher";
import { todayIso } from "../../../../../lib/business-dates";

type Product = components["schemas"]["Product"];
type BomLine = components["schemas"]["BomLine"];
type BomOutput = components["schemas"]["BomOutput"];
type BomLineInput = components["schemas"]["BomLineInput"];
type BomOutputInput = components["schemas"]["BomOutputInput"];
type ExplosionNode = components["schemas"]["BomExplosionNode"];
type BomSummary = components["schemas"]["BomSummary"];

// ---- dates ------------------------------------------------------------------

// The activation picker's floor is DELIBERATELY browser-local with server
// revalidation (`EFFECTIVE_DATE_PAST`, §9.2) — the D-089 tinting half, stated
// on the shared helper (src/lib/business-dates.ts). Re-exported so the dialog
// and `isPastDate` share one implementation instead of a local copy.
export { todayIso };

/** True when `date` is strictly before `floor` (both ISO dates). Back-dating an
 * activation is rejected by the API (`EFFECTIVE_DATE_PAST`, §9.2); the dialog
 * blocks it up front. */
export function isPastDate(date: string, floor: string = todayIso()): boolean {
  return date.length > 0 && date < floor;
}

// ---- editable models --------------------------------------------------------

export interface EditableSubstitute {
  key: string;
  substitute_product_id: string;
  priority: number;
  qty_factor: number;
}

export interface EditableLine {
  key: string;
  id: string | undefined;
  line_no: number;
  component_product_id: string;
  qty: number;
  uom_id: string;
  scrap_pct: number;
  consume_at_step_no: number | null;
  is_optional: boolean;
  notes: string | null;
  substitutes: EditableSubstitute[];
}

export interface EditableOutput {
  key: string;
  id: string | undefined;
  output_kind: "co_product" | "by_product";
  product_id: string;
  qty: number;
  uom_id: string;
  apportionment_pct: number | null;
  /** `undefined` = the field was redacted (§7: NRV is a cost signal). */
  nrv_per_price_unit_minor: number | null | undefined;
  nrv_price_unit_qty: number | null;
  currency: string | null;
}

let keySeq = 0;
/** Stable local key for a freshly added editable row (React list identity). */
export function freshKey(prefix: string): string {
  keySeq += 1;
  return `${prefix}-new-${keySeq}`;
}

export function fromBomLine(line: BomLine): EditableLine {
  return {
    key: `line-${line.id}`,
    id: line.id,
    line_no: line.line_no,
    component_product_id: line.component_product_id,
    qty: line.qty,
    uom_id: line.uom_id,
    scrap_pct: line.scrap_pct,
    consume_at_step_no: line.consume_at_step_no,
    is_optional: line.is_optional,
    notes: line.notes,
    substitutes: line.substitutes.map((s) => ({
      key: `sub-${s.id}`,
      substitute_product_id: s.substitute_product_id,
      priority: s.priority,
      qty_factor: s.qty_factor,
    })),
  };
}

export function fromBomOutput(output: BomOutput): EditableOutput {
  return {
    key: `output-${output.id}`,
    id: output.id,
    output_kind: output.output_kind,
    product_id: output.product_id,
    qty: output.qty,
    uom_id: output.uom_id,
    apportionment_pct: output.apportionment_pct,
    // Preserve the redaction signal: `undefined` (field omitted) must survive so
    // a no-cost role never fabricates an NRV value on save.
    nrv_per_price_unit_minor: output.nrv_per_price_unit_minor,
    nrv_price_unit_qty: output.nrv_price_unit_qty,
    currency: output.currency,
  };
}

/** Next line number on the SAP 10/20/30 grid (§2.7). */
export function nextLineNo(lines: readonly EditableLine[]): number {
  const max = lines.reduce((m, l) => Math.max(m, l.line_no), 0);
  return max + 10;
}

export function toLineInput(line: EditableLine): BomLineInput {
  return {
    line_no: line.line_no,
    component_product_id: line.component_product_id,
    qty: line.qty,
    uom_id: line.uom_id,
    scrap_pct: line.scrap_pct,
    consume_at_step_no: line.consume_at_step_no,
    is_optional: line.is_optional,
    notes: line.notes,
    substitutes: line.substitutes.map((s) => ({
      substitute_product_id: s.substitute_product_id,
      priority: s.priority,
      qty_factor: s.qty_factor,
    })),
  };
}

export function toOutputInput(output: EditableOutput): BomOutputInput {
  const base: BomOutputInput = {
    output_kind: output.output_kind,
    product_id: output.product_id,
    qty: output.qty,
    uom_id: output.uom_id,
  };
  if (output.output_kind === "co_product") {
    return { ...base, apportionment_pct: output.apportionment_pct };
  }
  // by_product — only forward NRV when it is present (not redacted), so a
  // no-cost role's save never overwrites the stored value with a guess (§7).
  const out: BomOutputInput = {
    ...base,
    nrv_price_unit_qty: output.nrv_price_unit_qty,
    currency: output.currency,
  };
  if (output.nrv_per_price_unit_minor !== undefined) {
    out.nrv_per_price_unit_minor = output.nrv_per_price_unit_minor;
  }
  return out;
}

// ---- co-product apportionment (donut) --------------------------------------

export interface ApportionmentSegment {
  key: string;
  productId: string;
  pct: number;
  /** Cumulative start offset in percent units (for the donut's dash offset). */
  cumulativeStart: number;
}

export interface ApportionmentResult {
  segments: ApportionmentSegment[];
  coProductTotal: number;
  /** The primary output's implied share: `100 − Σ co-product pct` (§2.9). */
  primaryRemainder: number;
  /** Σ co-product pct < 100 AND every pct in (0,100) — the activation rule
   * (§2.9, §9.5): Σ = 100 is invalid because the primary would get 0. */
  valid: boolean;
}

export function computeApportionment(
  coProducts: readonly { key: string; product_id: string; apportionment_pct: number | null }[],
): ApportionmentResult {
  const segments: ApportionmentSegment[] = [];
  let cumulative = 0;
  let total = 0;
  let allInRange = true;
  for (const cp of coProducts) {
    const raw = cp.apportionment_pct;
    const pct = raw === null || Number.isNaN(raw) ? 0 : raw;
    if (!(pct > 0 && pct < 100)) allInRange = false;
    const shown = Math.max(0, pct);
    segments.push({
      key: cp.key,
      productId: cp.product_id,
      pct: shown,
      cumulativeStart: cumulative,
    });
    cumulative += shown;
    total += pct;
  }
  const primaryRemainder = 100 - total;
  const valid = coProducts.length === 0 ? true : allInRange && total < 100;
  return { segments, coProductTotal: total, primaryRemainder, valid };
}

// ---- supersede preview (activation dialog) ---------------------------------

export interface SupersedePreview {
  supersededVersion: number | null;
  supersededId: string | null;
  /** An active version already covers/starts on/after the chosen date — the
   * activation would fail `VERSION_CONFLICT` rather than cleanly supersede
   * (§9.1). Surfaced as a blocking warning in the dialog. */
  conflict: boolean;
}

/**
 * Mirror the server's supersede decision (§3.3.4) for the dialog's preview: the
 * active version for the same facility scope whose effective range overlaps the
 * chosen `effectiveFrom` will be superseded (its `effective_to` set to
 * `effectiveFrom − 1`). A later-or-equal active start means a conflict, not a
 * clean supersede.
 */
export function previewSupersede(
  versions: readonly BomSummary[],
  draftFacilityId: string | null,
  effectiveFrom: string,
): SupersedePreview {
  const none: SupersedePreview = { supersededVersion: null, supersededId: null, conflict: false };
  if (effectiveFrom.length === 0) return none;
  const actives = versions.filter(
    (v) => v.status === "active" && v.facility_id === draftFacilityId && v.effective_from !== null,
  );
  for (const active of actives) {
    const from = active.effective_from;
    if (from === null) continue;
    const coversStart = from <= effectiveFrom;
    const coversEnd = active.effective_to === null || active.effective_to >= effectiveFrom;
    if (coversStart && coversEnd) {
      if (from >= effectiveFrom) {
        return { supersededVersion: active.version, supersededId: active.id, conflict: true };
      }
      return { supersededVersion: active.version, supersededId: active.id, conflict: false };
    }
    // An active version that starts strictly after the chosen date also blocks
    // a clean activation on that date.
    if (from > effectiveFrom) {
      return { supersededVersion: active.version, supersededId: active.id, conflict: true };
    }
  }
  return none;
}

// ---- phantom-inlined tree reconstruction -----------------------------------

export interface BomTreeNode {
  key: string;
  productId: string;
  sku: string | null;
  /** Net-of-scrap qty per 1 output unit (exploded leaves/intermediates); null
   * for a synthetic phantom row or an un-exploded root line. */
  qtyNet: number | null;
  uomId: string | null;
  /** A phantom that the explosion inlined (never emitted a node for) — badged
   * and expandable inline (§3.3.7, S3). */
  isPhantom: boolean;
  isOptional: boolean;
  /** The editable draft root line this node maps to, else null (deeper nodes
   * are read-only structural context). */
  rootLineNo: number | null;
  children: BomTreeNode[];
}

interface Rec {
  productId: string;
  sku: string | null;
  qtyNet: number | null;
  uomId: string | null;
  emitted: boolean;
  children: Map<string, Rec>;
}

/**
 * Reconstruct the display tree from the flat explosion (§5 GET /boms/:id?explode).
 * The explosion INLINES phantoms — it emits no node for a phantom, but every
 * emitted node's `path` still carries the phantom product ids for provenance
 * (bom-explosion.ts). So a product id that appears in some node's `path` but is
 * never itself emitted is a phantom: we synthesise a node for it and badge it,
 * restoring the "phantoms expand inline" view. The forest's top level is keyed
 * by the root component product id so {@link buildBomTree} can graft each root
 * line onto it.
 */
function explosionForest(
  nodes: readonly ExplosionNode[],
  productsById: ReadonlyMap<string, Product>,
): BomTreeNode[] {
  const root: Rec = {
    productId: "",
    sku: null,
    qtyNet: null,
    uomId: null,
    emitted: true,
    children: new Map(),
  };
  for (const n of nodes) {
    const full = [...n.path, n.component_product_id];
    let cur = root;
    for (const pid of full) {
      let child = cur.children.get(pid);
      if (child === undefined) {
        child = {
          productId: pid,
          sku: null,
          qtyNet: null,
          uomId: null,
          emitted: false,
          children: new Map(),
        };
        cur.children.set(pid, child);
      }
      cur = child;
    }
    cur.emitted = true;
    cur.sku = n.sku;
    cur.qtyNet = n.qty_net_of_scrap;
    cur.uomId = n.uom_id;
  }

  const convert = (rec: Rec, prefix: string): BomTreeNode => {
    const key = `${prefix}>${rec.productId}`;
    // A non-emitted intermediate can only be a phantom the explosion inlined.
    const isPhantom = !rec.emitted;
    return {
      key,
      productId: rec.productId,
      sku: rec.sku ?? productsById.get(rec.productId)?.sku ?? null,
      qtyNet: rec.qtyNet,
      uomId: rec.uomId,
      isPhantom,
      isOptional: false,
      rootLineNo: null,
      children: [...rec.children.values()].map((c) => convert(c, key)),
    };
  };

  return [...root.children.values()].map((c) => convert(c, "root"));
}

/**
 * The editor's left tree: the draft's own root lines (always present and
 * editable) with the explosion's structure grafted beneath each — phantoms
 * inlined and badged, deeper make-components nested. Degrades to a flat list of
 * root lines when the explosion is unavailable (e.g. a draft whose children have
 * no active BOM, or an explosion error), so the tree always renders.
 */
export function buildBomTree(
  rootLines: readonly EditableLine[],
  nodes: readonly ExplosionNode[],
  productsById: ReadonlyMap<string, Product>,
): BomTreeNode[] {
  const forest = explosionForest(nodes, productsById);
  const byProduct = new Map<string, BomTreeNode[]>();
  for (const node of forest) {
    const arr = byProduct.get(node.productId) ?? [];
    arr.push(node);
    byProduct.set(node.productId, arr);
  }
  const ordered = [...rootLines].sort((a, b) => a.line_no - b.line_no);
  return ordered.map((line) => {
    const match = byProduct.get(line.component_product_id)?.shift();
    if (match !== undefined) {
      return {
        ...match,
        key: `line-${line.line_no}`,
        isOptional: line.is_optional,
        rootLineNo: line.line_no,
        sku: match.sku ?? productsById.get(line.component_product_id)?.sku ?? null,
      };
    }
    return {
      key: `line-${line.line_no}`,
      productId: line.component_product_id,
      sku: productsById.get(line.component_product_id)?.sku ?? null,
      qtyNet: null,
      uomId: line.uom_id,
      isPhantom: false,
      isOptional: line.is_optional,
      rootLineNo: line.line_no,
      children: [],
    };
  });
}

// ---- version diff -----------------------------------------------------------

/** Adapt a version's BOM lines into the foundation {@link DiffRow} shape for the
 * side-by-side diff (S3). Keyed by `line_no` so the same slot compares across
 * versions; cost is deliberately excluded (structure diff only). */
export function linesToDiffRows(
  lines: readonly EditableLine[],
  productsById: ReadonlyMap<string, Product>,
  uomLabel: (uomId: string) => string,
  atStart: string,
): DiffRow[] {
  return [...lines]
    .sort((a, b) => a.line_no - b.line_no)
    .map((l) => ({
      key: String(l.line_no),
      label: productsById.get(l.component_product_id)?.sku ?? l.component_product_id,
      fields: {
        qty: l.qty,
        uom: uomLabel(l.uom_id),
        scrap_pct: l.scrap_pct,
        consume_at_step: l.consume_at_step_no ?? atStart,
        optional: l.is_optional,
        substitutes: l.substitutes.length,
      },
    }));
}
